import {
    Component,
    ElementRef,
    inject,
    input,
    OnChanges,
    OnInit,
    SimpleChanges,
    TemplateRef,
} from '@angular/core'
import { FormGroup } from '@angular/forms'
import { MatExpansionModule } from '@angular/material/expansion'
import { TbAccordionComponent } from '@components-library/tb-accordion/tb-accordion.component'
import { compare, getBoolean } from '@core/global-util'
import { AppRecord, Field, FieldType, FieldTypes, isWorkspaceGlobal, Workspace } from '@core/models'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Dictionary } from '@ngrx/entity'
import { cloneDeep, keyBy } from 'lodash-es'
import { TbTagComponent } from '../../@components-library/tb-tags/components/tb-tag/tb-tag.component'
import { NgTemplateOutlet } from '@angular/common'
import { TranslocoModule } from '@jsverse/transloco'
import { FieldFacadeService } from '@services/store-facade'

export interface AccordionContentContext {
    fieldTypes: Dictionary<FieldType>
    workspaces: { [workspaceGuid: string]: Workspace }
    fields: Field[]
    values: { [fieldGuid: string]: string }
    record?: AppRecord
    form?: FormGroup
    groupName: string
}

type FieldKeys = keyof Field

@UntilDestroy()
@Component({
    selector: 'app-fields-collection',
    templateUrl: './fields-collection.component.html',
    imports: [
        TranslocoModule,
        NgTemplateOutlet,
        TbTagComponent,
        MatExpansionModule,
        TbAccordionComponent,
    ],
})
export class FieldsCollectionComponent implements OnInit, OnChanges {
    private readonly elementRef = inject(ElementRef)

    private readonly sortingFieldKeys: FieldKeys[] = ['is_on_top', 'is_required']
    private readonly knownGroupNames: string[] = ['required', 'shared']

    readonly accordionContent = input.required<TemplateRef<AccordionContentContext>>()
    readonly form = input<FormGroup>()
    private readonly fieldFacadeService = inject(FieldFacadeService)

    private readonly fields$ = this.fieldFacadeService.selectFieldsArrayBySchema$

    // could be not equal to store selected workspace during record creation
    readonly selectedWorkspace = input.required<Workspace>()
    readonly availableWorkspaces = input<Workspace[]>()
    readonly recordFieldValues = input<{ [fieldGuid: string]: string }>({})
    readonly record = input<AppRecord>()
    readonly excludedFieldTypes = input([
        FieldTypes.WATCH,
        FieldTypes.NAME,
        FieldTypes.RICH_TEXT,
        FieldTypes.LINK,
        FieldTypes.LINK_REFERENCE,
    ] as string[])
    readonly excludedFieldsGuids = input<string[]>([])
    readonly workspaces = input.required<Workspace[]>()
    readonly selectedSchema = input<string>()
    readonly fieldTypes = input.required<Dictionary<FieldType>>()
    readonly isSystemFieldsAvailable = input(false)
    readonly pinWorkspaces = input(false)

    fields!: Record<string, Field[]>
    allFields: Field[] = []
    visibleWorkspaces!: Workspace[]
    fieldsByWorkspace: { [guid: string]: Field[] } = {}
    sharedFields: Field[] = []
    requiredOrTopFields: Field[] = []
    visibleWorkspacesByKey: { [p: string]: Workspace } = {}
    workspacesByKey: { [p: string]: Workspace } = {}

    get isOnlyGlobalInList() {
        if (this.visibleWorkspaces?.length === 1) {
            return isWorkspaceGlobal(this.visibleWorkspaces[0])
        }

        return false
    }

    get workspaceCount() {
        const visibleWorkspacesLength = this.visibleWorkspaces.length ?? 0
        if (!this.isOnlyGlobalInList && this.sharedFields.length) {
            return visibleWorkspacesLength + 1
        }
        return visibleWorkspacesLength
    }

    ngOnInit(): void {
        this.fields$.pipe(untilDestroyed(this)).subscribe((fields) => {
            this.fields = fields
            this.setState(fields)

            this.visibleWorkspacesByKey = keyBy(this.visibleWorkspaces, 'guid')
            this.workspacesByKey = keyBy(this.workspaces(), 'guid')
        })
    }

    getAccordionContentContext(groupName: string, fields: Field[]): AccordionContentContext {
        return {
            fieldTypes: this.fieldTypes(),
            workspaces: this.knownGroupNames.includes(groupName)
                ? this.workspacesByKey
                : this.visibleWorkspacesByKey,
            fields,
            values: this.recordFieldValues(),
            record: this.record(),
            form: this.form(),
            groupName,
        }
    }

    scrollToExpansionPanel(guid: string) {
        const htmlPanel = (this.elementRef.nativeElement as HTMLElement).querySelector(`#${guid}`)

        if (htmlPanel) {
            htmlPanel.scrollIntoView({ behavior: 'smooth' })
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedSchema && !changes.selectedSchema.firstChange) {
            this.setState(this.fields)
            this.visibleWorkspacesByKey = keyBy(this.visibleWorkspaces, 'guid')
        }

        if (changes.availableWorkspaces && !changes.availableWorkspaces.firstChange) {
            const availableWorkspaces = this.availableWorkspaces()
            if (availableWorkspaces) {
                this.visibleWorkspaces =
                    availableWorkspaces.filter(
                        (workspace) => this.fieldsByWorkspace[workspace.guid].length,
                    ) || []
            }
            this.requiredOrTopFields = this.getRequiredOrTopFields(this.allFields)
            this.visibleWorkspacesByKey = keyBy(this.visibleWorkspaces, 'guid')
        }

        if (changes.workspaces && !changes.workspaces.firstChange) {
            this.workspacesByKey = keyBy(this.workspaces(), 'guid')
        }
    }

    workspaceLayoutClass(workspaceCount: number) {
        const classCol: Record<number, string> = {
            2: 'grid-cols-2',
            3: 'grid-cols-3',
        }

        const stickyBookmarks = this.pinWorkspaces() ? 'sticky top-4' : ''

        if (classCol[workspaceCount]) {
            return `${stickyBookmarks} grid  ${classCol[workspaceCount]}`
        }
        return `${stickyBookmarks} flex flex-wrap`
    }

    tagClass(workspaceCount: number) {
        if (workspaceCount > 3) {
            return 'w-auto'
        }
        return 'w-full'
    }

    private setState(fields: Record<string, Field[]>) {
        this.setFields(fields)
        const availableWorkspaces = this.availableWorkspaces()
        if (availableWorkspaces) {
            this.visibleWorkspaces = availableWorkspaces.filter(
                (workspace) => this.fieldsByWorkspace[workspace.guid].length,
            )
        }
        this.visibleWorkspaces = this.setVisibleWorkspaces()
    }

    private setVisibleWorkspaces() {
        if (isWorkspaceGlobal(this.selectedWorkspace())) {
            return this.workspaces().filter(
                (workspace) => this.fieldsByWorkspace[workspace.guid].length,
            )
        }

        return this.workspaces().filter((workspace) => {
            if (isWorkspaceGlobal(workspace)) {
                return true
            }

            if (!this.fieldsByWorkspace[workspace.guid].length) {
                return false
            }

            return workspace.guid === this.selectedWorkspace().guid
        })
    }

    protected setFields(fields: Record<string, Field[]>) {
        if (!this.selectedSchema || !this.workspaces) return

        const schemaFields = fields[this.selectedSchema()!]

        if (!schemaFields) return

        this.allFields = cloneDeep(schemaFields).sort((a, b) => compare(a.name, b.name))

        this.sharedFields = this.getSharedFields(this.allFields)
        this.requiredOrTopFields = this.getRequiredOrTopFields(this.allFields)

        this.workspaces().reduce((fieldsByWorkspace, workspace) => {
            fieldsByWorkspace[workspace.guid] = this.getFieldsByWorkspace(this.allFields, workspace)
            return fieldsByWorkspace
        }, this.fieldsByWorkspace)
    }

    protected getRequiredOrTopFields(fields: Field[]) {
        return fields
            .filter(
                (field) =>
                    (!!field.is_required || !!field.is_on_top) &&
                    this.checkByWorkspace(field) &&
                    !this.excludedFieldTypes().includes(field.field_type_code) &&
                    !this.excludedFieldsGuids().includes(field.guid),
            )
            .sort((a, b) => this.sortFields(a, b, this.sortingFieldKeys))
    }

    private checkByWorkspace(field: Field) {
        if (isWorkspaceGlobal(this.selectedWorkspace())) return true

        if (getBoolean(field.workspace_name?.is_global)) return true

        const availableWorkspaces = this.availableWorkspaces()
        if (availableWorkspaces) {
            return !!availableWorkspaces.find(
                (workspace) => workspace.guid === field.workspace_guid,
            )
        } else {
            return this.selectedWorkspace().guid === field.workspace_guid
        }
    }

    protected sortFields(a: Field, b: Field, sortingFieldKeys: FieldKeys[]) {
        let compareResult = 0
        sortingFieldKeys.forEach((fieldKey) => {
            const isAsc = false
            if (!compareResult) {
                const compared = compare(
                    a[fieldKey] as number | undefined,
                    b[fieldKey] as number | undefined,
                    isAsc,
                )
                if (compared !== 0) {
                    compareResult = compared
                }
            }
        })
        return compareResult
    }

    private getFieldsByWorkspace(fields: Field[], workspace: Workspace) {
        return fields.filter(
            (field) =>
                !field.is_required &&
                !field.is_on_top &&
                !this.excludedFieldTypes().includes(field.field_type_code) &&
                !this.excludedFieldsGuids().includes(field.guid) &&
                (field.workspace_guid === workspace.guid ||
                    (this.isSystemFieldsAvailable() &&
                        field.is_readonly &&
                        getBoolean(field.workspace_name?.is_global) &&
                        isWorkspaceGlobal(workspace))),
        )
    }

    private getSharedFields(fields: Field[]) {
        return fields.filter((field) => {
            const notInAvailableWorkspace = !this.visibleWorkspaces?.find(
                (workspace) => field.workspace_guid === workspace.guid,
            )
            const notExcludedType = !this.excludedFieldTypes().includes(field.field_type_code)
            const notExcludedField = !this.excludedFieldsGuids().includes(field.guid)
            const sharedInAvailableWorkspace = this.isSharedInAvailableWorkspace(field)

            return (
                !field.is_required &&
                notExcludedType &&
                notExcludedField &&
                notInAvailableWorkspace &&
                sharedInAvailableWorkspace
            )
        })
    }

    private isSharedInAvailableWorkspace(field: Field) {
        const availableWorkspaces = this.availableWorkspaces()
        if (availableWorkspaces) {
            return !!availableWorkspaces.find((workspace) =>
                field.shared_with_workspaces?.includes(workspace.guid),
            )
        }
        return !!field.shared_with_workspaces?.includes(this.selectedWorkspace().guid)
    }
}
